import { EyeIcon, LoaderCircleIcon, PencilIcon, SlidersHorizontalIcon, UsersIcon } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { toast } from 'sonner'

import { type QuoteAllowanceParams, useCreateQuoteAllowances } from 'components/hooks/quotes/useCreateQuoteAllowances'
import useFlatAllowanceCalculations from 'components/hooks/quotes/useFlatAllowanceCalculations'
import { type QuoteAllowance } from 'components/prospects/types'
import { EditableQuoteTitle } from 'components/quote_explorer/EditableQuoteTitle'
import EmployeeDetailsSheet from 'components/quote_explorer/EmployeeDetailsSheet'
import { QuoteExplorerMode } from 'components/quote_explorer/QuoteExplorer'
import { QuoteProvider } from 'components/quote_explorer/QuoteProvider'
import { QuotingEmployeeProvider } from 'components/quoting/employees/QuotingEmployeeContext'
import QuoteFlowHeader from 'components/quoting/QuoteFlowHeader'
import { SkeletonItem } from 'components/reusable/SkeletonItem'
import { Banner, Button, TooltipProvider } from 'slab'
import { type MoneyAmount } from 'types'
import { useGetQuotingQuoteResultsQuoteId, useGetQuotingQuoteResultsQuoteIdFlatAllowanceResults as useGetFlatAllowanceResults } from 'utils/web_api'
import { type QuotingEmployee, type QuotingQuoteResult } from 'utils/web_api.schemas'

import AllowanceConfiguration from './AllowanceConfiguration'
import AllowanceTabs, { type AllowanceTab } from './AllowanceTabs'
import Employees from './Employees'
import SidePanel from './SidePanel'
import { QuotePricingProvider, useQuotePricing } from '../pricing/QuotePricingProvider'

type Props = {
  prospectId: string
  quoteId: string
  allowances?: QuoteAllowance[]
  prospectName: string
  acceptQuoteUrl?: string
  canEditQuote: boolean
  canOnboardFromQuote: boolean
  canShareQuote: boolean
  previousStepPath?: string
  prospectAssociatedWithEmployer: boolean
  workflowType: 'broker' | 'sales' | 'public'
  initialMode?: QuoteExplorerMode
  canSendEditorInvites: boolean
  canSendOnboardInvites: boolean
}

export type Allowance = QuoteAllowanceParams

export type Allowances = {
  allowances: Allowance[]
}

export type AllowanceWithIndex = Allowance & {
  index: number
}

export const getDefaultAllowance = (): Allowance => ({
  flat_config: {
    employee_amount: 550,
    spouse_amount: 550,
    dependent_amount: 250,
    max_dependent_count: 10,
  },
})

const isFlatAllowanceValid = (allowance: QuoteAllowance): allowance is QuoteAllowance & {
  employee_amount: MoneyAmount;
  spouse_amount: MoneyAmount;
  dependent_amount: MoneyAmount;
  max_dependent_count: number;
} => {
  return (
    typeof allowance.employee_amount === 'object' &&
    typeof allowance.spouse_amount === 'object' &&
    typeof allowance.dependent_amount === 'object' &&
    typeof allowance.max_dependent_count === 'number'
  )
}

const getAllowanceFromQuoteAllowance = (quoteAllowance: QuoteAllowance): Allowance | null => {
  if (!isFlatAllowanceValid(quoteAllowance)) {
    return null
  }

  const allowanceClass = quoteAllowance.location_id && quoteAllowance.location_type
    ? {
        location_id: quoteAllowance.location_id,
        location_type: quoteAllowance.location_type,
      }
    : undefined

  return {
    class: allowanceClass,
    flat_config: {
      employee_amount: Number(quoteAllowance.employee_amount.value),
      spouse_amount: Number(quoteAllowance.spouse_amount.value),
      dependent_amount: Number(quoteAllowance.dependent_amount.value),
      max_dependent_count: quoteAllowance.max_dependent_count,
    },
  }
}

const getAllowancesFromQuoteAllowances = (quoteAllowances: QuoteAllowance[]): Allowance[] => {
  return quoteAllowances.map(getAllowanceFromQuoteAllowance).filter(Boolean) as Allowance[]
}

function FlatAllowanceExplorer ({
  prospectId,
  quoteId,
  allowances,
  prospectName,
  acceptQuoteUrl,
  canEditQuote,
  canOnboardFromQuote,
  canShareQuote,
  previousStepPath,
  prospectAssociatedWithEmployer,
  workflowType,
  initialMode = QuoteExplorerMode.explore,
  canSendEditorInvites,
  canSendOnboardInvites,
}: Props) {
  const [savedAllowances, setSavedAllowances] = useState<Allowance[]>(getAllowancesFromQuoteAllowances(allowances || []))
  const [mode, setMode] = useState<QuoteExplorerMode>(initialMode)
  const [quoteProcessing, setQuoteProcessing] = useState(true)
  const [error, setError] = useState('')
  const isExploreMode = mode === QuoteExplorerMode.explore

  const { setQuoteType } = useQuotePricing()

  const { data: quote, isLoading: isQuoteLoading } = useGetQuotingQuoteResultsQuoteId(quoteId, {
    query: {
      refetchInterval: (query) => {
        if (query.state.error || ['processed', 'failed'].includes(query.state.data?.status ?? '')) {
          return false
        }
        return 1_000
      },
    },
  })

  const { data: employees, isLoading: isLoadingEmployees } = useGetFlatAllowanceResults(quoteId, {
    query: {
      enabled: quote?.status === 'processed',
    },
  })
  const createQuoteAllowancesMutation = useCreateQuoteAllowances({ prospectId, quoteId, removeExisting: true })

  useEffect(() => {
    if (quote?.status === 'failed') {
      setQuoteProcessing(false)
      setError(quote.quote_errors?.join(', ') ?? 'Please return to the previous step and try again.')
    } else if (quote?.status === 'processed') {
      setQuoteProcessing(false)
      setQuoteType(quote.quote_type)
    }
  }, [quote])

  const methods = useForm<Allowances>({
    mode: 'onBlur',
    defaultValues: {
      allowances: allowances?.length ? getAllowancesFromQuoteAllowances(allowances) : [getDefaultAllowance()],
    },
  })

  const eligibleEmployees = useMemo(() => {
    return employees?.filter((employee) => employee.eligible) ?? []
  }, [employees])

  const employeesById = useMemo(() => {
    return employees?.reduce((acc, employee) => {
      acc[employee.id] = employee
      return acc
    }, {} as Record<string, QuotingQuoteResult>) ?? {}
  }, [employees])

  const { costsByEmployeeId } = useFlatAllowanceCalculations(eligibleEmployees || [], methods.watch('allowances'))

  const monthlyAllowanceForEmployee = useCallback((employee: QuotingEmployee) => {
    const cost = costsByEmployeeId[employee.id]

    return cost?.employerCost ?? 0
  }, [costsByEmployeeId])

  const monthlyEmployeeOutOfPocketContribution = useCallback((employee: QuotingEmployee) => {
    const cost = costsByEmployeeId[employee.id]
    return cost?.employeeCost ?? 0
  }, [costsByEmployeeId])

  const minAffordableAllowanceForEmployee = useCallback((employee: QuotingEmployee) => {
    const employeeData = employeesById[employee.id]
    return employeeData?.min_employer_contribution
  }, [employees])

  const isAllowanceAffordable = useCallback((employee: QuotingEmployee) => {
    const cost = costsByEmployeeId[employee.id]
    const employeeData = employeesById[employee.id]

    return (cost?.employeeAllowanceAmount ?? 0) >= (employeeData?.min_employer_contribution ?? 0)
  }, [costsByEmployeeId])

  const { employeeCount, stateCount } = useMemo(() => {
    return {
      employeeCount: eligibleEmployees?.length,
      stateCount: new Set(eligibleEmployees?.map(employee => employee.rating_area_code.substring(0, 2))).size,
    }
  }, [eligibleEmployees])

  const tabs: AllowanceTab[] = [
    {
      id: 'allowances',
      label: 'Configure allowances',
      icon: SlidersHorizontalIcon,
      content: <AllowanceConfiguration mode={mode} previousStepPath={previousStepPath} />,
    },
    {
      id: 'employees',
      label: 'View employees',
      icon: UsersIcon,
      content: <Employees employees={eligibleEmployees || []} isLoading={isLoadingEmployees || quoteProcessing} />,
    },
  ]

  const onSaveQuote = async (allowances: Allowances) => {
    try {
      await createQuoteAllowancesMutation.mutateAsync(allowances.allowances)
      setSavedAllowances(allowances.allowances)
      methods.reset(allowances)
      toast.success('Quote saved!')
    } catch {
      toast.error('Failed to save quote')
    }
  }

  // Show loading state while quote data is initially loading
  if (isQuoteLoading && !quote) {
    return (
      <div className='flex flex-row w-full h-full bg-white'>
        <div className='flex flex-col w-full overflow-y-auto'>
          <div className='p-6 pb-0'>
            <div className='flex justify-between gap-x-2'>
              <QuoteFlowHeader
                title={<SkeletonItem heightClass='h-8' widthClass='w-64' />}
                description={<SkeletonItem heightClass='h-3' widthClass='w-48' />}
              />
            </div>
          </div>
          <div className='flex items-center justify-center h-64'>
            <div className='flex flex-col items-center gap-4'>
              <LoaderCircleIcon className='w-8 h-8 animate-spin text-thatch-500' />
              <p className='text-lg font-medium text-gray-700'>Loading quote...</p>
            </div>
          </div>
        </div>
        <div className='flex flex-col w-[416px] min-w-[416px] bg-gray-50'>
          <div className='flex items-center justify-center h-full'>
            <div className='flex flex-col items-center gap-4'>
              <LoaderCircleIcon className='w-6 h-6 animate-spin text-thatch-500' />
              <p className='text-sm text-gray-500'>Loading...</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <QuoteProvider quote={quote}>
      <QuotePricingProvider quoteId={quoteId} workflowType={workflowType}>
        <QuotingEmployeeProvider
          employees={eligibleEmployees || []}
          monthlyAllowanceForEmployee={monthlyAllowanceForEmployee}
          monthlyEmployeeOutOfPocketContribution={monthlyEmployeeOutOfPocketContribution}
          minAffordableAllowanceForEmployee={minAffordableAllowanceForEmployee}
          isAllowanceAffordable={isAllowanceAffordable}
        >
          <div className='flex flex-row w-full h-full bg-white relative'>
            {quoteProcessing && (
              <div className='absolute inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center'>
                <div className='flex flex-col items-center gap-4'>
                  <LoaderCircleIcon className='w-8 h-8 animate-spin text-thatch-500' />
                  <p className='text-lg font-medium text-gray-700'>Processing quote...</p>
                  <p className='text-sm text-gray-500'>This may take a few moments</p>
                </div>
              </div>
            )}
            <FormProvider {...methods}>
              <div className='flex flex-col w-full overflow-y-auto'>
                <div className='p-6 pb-0'>
                  <div className='flex justify-between gap-x-2'>
                    <QuoteFlowHeader
                      title={
                        <EditableQuoteTitle
                          quote={quote}
                          prospectId={prospectId}
                          quoteId={quoteId}
                          prospectName={prospectName}
                          mode={mode}
                          isLoading={isQuoteLoading}
                        />
                      }
                      titleAsChild
                      description={employeeCount && stateCount
                        ? `${employeeCount} employee${employeeCount === 1 ? '' : 's'} in ${stateCount} state${stateCount === 1 ? '' : 's'}`
                        : <SkeletonItem heightClass='h-3' widthClass='w-48' />}
                    />
                    {canEditQuote && (
                      <Button
                        variant='secondary'
                        size='small'
                        onClick={() => setMode(prev => prev === QuoteExplorerMode.explore ? QuoteExplorerMode.edit : QuoteExplorerMode.explore)}
                      >
                        <div className='flex items-center gap-1'>
                          {isExploreMode && <><PencilIcon className='w-4 h-4' /> Edit</>}
                          {!isExploreMode && <><EyeIcon className='w-4 h-4' />Explore</>}
                        </div>
                      </Button>
                    )}
                  </div>
                </div>
                <AllowanceTabs defaultValue='allowances' tabs={tabs} />
                {error && (
                  <div className='p-6 pt-0'>
                    <Banner type='critical' title='Error generating quote' onDismiss={() => setError('')}>
                      <p>
                        {error}
                      </p>
                    </Banner>
                  </div>
                )}
              </div>
              <div className='flex flex-col w-[416px] min-w-[416px] bg-gray-50'>
                <SidePanel
                  quoteId={quoteId}
                  prospectName={prospectName}
                  employees={eligibleEmployees}
                  isLoadingEmployees={isLoadingEmployees || quoteProcessing}
                  savedAllowances={savedAllowances}
                  acceptQuoteUrl={acceptQuoteUrl}
                  mode={mode}
                  canOnboardFromQuote={canOnboardFromQuote}
                  canShareQuote={canShareQuote}
                  onClickSaveQuote={() => methods.handleSubmit(onSaveQuote)()}
                  prospectAssociatedWithEmployer={prospectAssociatedWithEmployer}
                  canSendEditorInvites={canSendEditorInvites}
                  canSendOnboardInvites={canSendOnboardInvites}
                />
              </div>
            </FormProvider>
          </div>
          <EmployeeDetailsSheet />
        </QuotingEmployeeProvider>
      </QuotePricingProvider>
    </QuoteProvider>
  )
}

export default (props: Props) => {
  return (
    <TooltipProvider>
      <FlatAllowanceExplorer {...props} />
    </TooltipProvider>
  )
}
